import { useRef, useEffect } from 'react'
import { lerp, clamp } from 'lib/helpers'
import { urlForImage } from 'lib/sanity.image'
import { WorldHeader } from 'components/World/WorldHeader'

import Image from 'next/image'
import clsx from 'clsx'
import Link from 'next/link'
import { PortableText } from '@portabletext/react'

export const WorldBlock = ({ world, image, title, link }) => {
  const worldRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const raf = useRef<any>(null)
  const position = useRef({
    now: { x: 0, y: 0 },
    target: { x: 0, y: 0 },
  })

  const touchStart = useRef({ x: 0, y: 0 })
  const lastTouchPos = useRef({ x: 0, y: 0 })
  const touchPos = useRef({
    now: { x: 0, y: 0 },
    target: { x: 0, y: 0 },
  })

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      containerRef.current &&
      worldRef.current
    ) {
      if (window.innerWidth < 768) {
        const items = worldRef.current.querySelectorAll('.world-item')
        const worldRect = worldRef.current.getBoundingClientRect()
        touchPos.current.now = {
          x: worldRect.width / 2 - window.innerWidth / 2,
          y: 0,
        }
        touchPos.current.target = {
          x: worldRect.width / 2 - window.innerWidth / 2,
          y: 0,
        }

        touchStart.current = {
          x: worldRect.width / 2 - window.innerWidth / 2,
          y: 0,
        }

        lastTouchPos.current = {
          x: worldRect.width / 2 - window.innerWidth / 2,
          y: 0,
        }
      }
    }

    const onMouseMove = (e) => {
      if (!worldRef.current || !containerRef.current) return
      const { clientX, clientY } = e
      const { offsetWidth, offsetHeight } = worldRef.current
      const { offsetWidth: containerWidth, offsetHeight: containerHeight } =
        containerRef.current
      const maxOffset = offsetWidth - containerWidth
      const maxOffsetY = offsetHeight - containerHeight
      const offsetX = (clientX / containerWidth) * maxOffset
      const offsetY = (clientY / containerHeight) * maxOffsetY

      const x = (clientX - containerWidth / 2) / (containerWidth / 2)
      const y = (clientY - containerHeight / 2) / (containerHeight / 2)

      position.current.target.x = offsetX
      position.current.target.y = offsetY
    }

    const animate = () => {
      if (!worldRef.current) return
      const items = worldRef.current.querySelectorAll('.world-item')

      if (touchStart.current.x !== 0) {
        const x = lerp(touchPos.current.now.x, touchPos.current.target.x, 0.04)
        const y = lerp(touchPos.current.now.y, touchPos.current.target.y, 0.04)
        touchPos.current.now.x = x
        touchPos.current.now.y = y

        items?.forEach((item: any) => {
          const threshold = item.dataset.threshold
          item.style.transform = `translate3d(-${x * 1}px, -${y * 1}px, 0)`
        })
        return (raf.current = requestAnimationFrame(animate))
      }

      const x = lerp(position.current.now.x, position.current.target.x, 0.04)
      const y = lerp(position.current.now.y, position.current.target.y, 0.04)
      position.current.now.x = x
      position.current.now.y = y

      items?.forEach((item: any) => {
        const threshold = item.dataset.threshold
        item.style.transform = `translate3d(-${x * threshold}px, -${
          y * threshold
        }px, 0)`
      })

      raf.current = requestAnimationFrame(animate)
    }

    const onTouchStart = (e) => {
      if (!worldRef.current || !containerRef.current) return
      const { clientX, clientY } = e.touches[0]
      touchStart.current.x = clientX
      touchStart.current.y = clientY
    }

    const onTouchMove = (e) => {
      if (!worldRef.current || !containerRef.current) return
      const { clientX, clientY } = e.touches[0]
      const xMoved = touchStart.current.x - clientX

      const x = clamp(
        lastTouchPos.current.x + xMoved,
        0,
        worldRef.current.offsetWidth - containerRef.current.offsetWidth
      )

      touchPos.current.target = {
        x: x,
        y: 0,
      }
    }

    const onTouchEnd = (e) => {
      if (!worldRef.current || !containerRef.current) return
      lastTouchPos.current = touchPos.current.now
    }

    raf.current = requestAnimationFrame(animate)

    window.addEventListener('mousemove', onMouseMove)
    if (containerRef.current) {
      containerRef.current.addEventListener('touchstart', onTouchStart)
      containerRef.current.addEventListener('touchmove', onTouchMove)
      containerRef.current.addEventListener('touchend', onTouchEnd)
    }

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      cancelAnimationFrame(raf.current)
    }
  }, [])

  return (
    <div className="w-full overflow-hidden py-50">
      <div ref={containerRef} className="relative h-[110vh] w-full px-60 pt-50">
        <div
          ref={worldRef}
          className={`relative grid h-full w-[450%] grid-cols-5 will-change-auto md:left-0 md:top-0 md:h-[112%] md:w-[130%]`}
        >
          <WorldItem image={image} title={title} link={link} />
          {world.items.map((item, index) => {
            if (index > 8) return null
            return <Item key={item._key} {...item} index={index} />
          })}
        </div>
      </div>
    </div>
  )
}

const positionMap = {
  0: { top: 0, left: 0, threshold: 0.75 },
  1: { top: 28, left: 13, threshold: 0.62 },
  2: { top: 65, left: 24, threshold: 0.62 },
  3: { top: 1, left: 31, threshold: 0.55 },
  4: { top: 33, left: 39, threshold: 0.42 },
  5: { top: 64, left: 50, threshold: 0.62 },
  6: { top: 60, left: 1, threshold: 0.98 },
  7: { top: 3, left: 72, threshols: 0.88 },
  8: { top: 63, left: 78, threshols: 0.85 },
}

const WorldItem = ({ image, title, link }) => {
  const top = 36
  const left = 58
  const threshold = 0.5
  return (
    <Link
      scroll={false}
      href={'/world'}
      className={`item world-item absolute w-[120px] cursor-pointer overflow-hidden text-center will-change-auto md:w-[180px]`}
      data-threshold={threshold}
      style={{
        top: `${top}%`,
        left: `${left}%`,
      }}
    >
      <div className="relative" style={{ aspectRatio: image.aspect }}>
        <Image
          src={urlForImage(image).width(300).auto('format').url()}
          fill
          style={{ objectFit: 'cover' }}
          sizes="(max-width: 767px) 120px, 180px"
          alt=""
        />
      </div>
      <div className="mt-10 inline-block rounded-4 bg-black px-6 py-4 text-12 font-bold text-white">
        {title}
      </div>
    </Link>
  )
}

const Item = (item) => {
  const { gridImage, _type, index } = item
  const position = positionMap[index]
  const top = position?.top ?? 0
  const left = position?.left ?? 0
  const threshold = position?.threshold ?? 1
  const classes = clsx(
    { 'w-[220px] md:w-[300px] aspect-video': _type === 'tv' },
    { 'w-[115px] md:w-[175px] aspect-[1/1.2]': _type === 'article' },
    { 'w-[100px] md:w-[150px] aspect-[10/16]': _type === 'socialVideo' },
    { 'w-[115px] md:w-[175px] aspect-[1/1.2]': _type === 'thread' },
    { 'w-[115px] md:w-[175px] aspect-[1/1.2]': _type === 'lookbook' }
  )
  const href = `/world?item=${item._key}`
  return (
    <Link
      scroll={false}
      href={href}
      className="world-item group absolute z-[6] cursor-pointer will-change-auto hover:z-[7]"
      data-threshold={threshold}
      style={{
        top: `${top}%`,
        left: `${left}%`,
      }}
    >
      <div className={`relative overflow-hidden rounded-12 ${classes}`}>
        <Image
          src={urlForImage(gridImage).width(1200).auto('format').url()}
          fill
          style={{ objectFit: 'cover' }}
          sizes="(max-width: 767px) 200px, 400px"
          alt=""
        />
      </div>
      <div className="absolute left-[calc(100%-90px)] top-[calc(100%-30px)] hidden rounded-6 bg-whiter p-14 text-12 text-midGray opacity-0 shadow-md transition-opacity duration-300 group-active:opacity-100 md:block md:group-hover:opacity-100">
        {_type === 'thread' && <ThreadOverlay {...item} href={href} />}
        {_type === 'article' && <ArticleOverlay {...item} href={href} />}
        {_type === 'tv' && <TVOverlay {...item} href={href} />}
        {_type === 'socialVideo' && (
          <SocialVideoOverlay {...item} href={href} />
        )}
        {_type === 'lookbook' && <LookbookOverlay {...item} href={href} />}
      </div>
    </Link>
  )
}

const ArticleOverlay = (item) => {
  return (
    <div className="w-[160px]">
      <WorldHeader {...item} />
      <div>{item.title}</div>
      <div className="text-gray hover:underline">Read more</div>
    </div>
  )
}

const TVOverlay = (item) => {
  return (
    <div className="w-[165px]">
      <WorldHeader
        avatar={item.avatar}
        author={item.handle}
        published={item.published}
      />
      <div>{item.title}</div>
      <div className="text-gray hover:underline">Watch now</div>
    </div>
  )
}

const SocialVideoOverlay = (item) => {
  return (
    <div className="w-[240px]">
      <WorldHeader {...item} />
      <div>
        <PortableText value={item.caption} />
      </div>
      <div className="text-gray hover:underline">View more</div>
    </div>
  )
}

const ThreadOverlay = (item) => {
  return (
    <div className="w-[200px]">
      <WorldHeader {...item} />
      <div>{item.title}</div>
      <div className="text-gray hover:underline">Read more</div>
    </div>
  )
}

const LookbookOverlay = (item) => {
  return (
    <div className="w-[165px]">
      <WorldHeader {...item} />
      <div>{item.title}</div>
      <div className="text-gray hover:underline">View more</div>
    </div>
  )
}
